import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, PLATFORM_ID, inject, signal } from '@angular/core';
import { I18nService } from './i18n/i18n.service';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { InboxItem, InboxService } from './inbox.service';

/**
 * Reusable in-app notification bell for the orderYaa staff surfaces (partner, rider). Shows a polled
 * unread badge and a dropdown of recent notifications with click-to-mark-read. Parameterized by the
 * endpoint {@link base} so partner and rider reuse one component. Signal-based (orderYaa is zoneless)
 * and SSR-safe (polls only in the browser). Render it only when the surface is authenticated — its
 * own auth interceptor attaches the JWT to the calls.
 */
@Component({
  selector: 'app-inbox-bell',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './inbox-bell.html',
  styleUrl: './inbox-bell.css',
})
export class InboxBell implements OnInit, OnDestroy {
  protected readonly i18n = inject(I18nService);
  /** Inbox endpoint base, e.g. `${API_BASE}/api/notifications/staff/inbox`. */
  @Input({ required: true }) base!: string;

  /** Optional deep-link: when set, clicking an order notification navigates here (e.g. '/orders'). */
  @Input() ordersRoute?: string;

  private readonly inbox = inject(InboxService);
  private readonly router = inject(Router);
  private readonly elRef = inject(ElementRef);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  readonly open = signal(false);
  readonly items = signal<InboxItem[]>([]);
  readonly unread = signal(0);
  readonly loading = signal(false);
  readonly error = signal(false);

  private pollHandle: ReturnType<typeof setInterval> | null = null;
  private readonly POLL_MS = 60_000;

  ngOnInit(): void {
    if (!this.isBrowser || !this.base) return;
    this.refreshCount();
    this.pollHandle = setInterval(() => this.refreshCount(), this.POLL_MS);
  }

  ngOnDestroy(): void {
    if (this.pollHandle) clearInterval(this.pollHandle);
  }

  toggle(event?: MouseEvent): void {
    event?.stopPropagation();
    const next = !this.open();
    this.open.set(next);
    if (next) this.loadList();
  }

  /** Click a notification: mark it read, then deep-link to its target (if ordersRoute is set + it has one). */
  onItemClick(item: InboxItem, event?: MouseEvent): void {
    event?.stopPropagation();
    this.markRead(item);
    if (this.ordersRoute && this.hasOrder(item)) {
      this.open.set(false);
      this.router.navigateByUrl(this.ordersRoute);
    }
  }

  markRead(item: InboxItem): void {
    if (item.read) return;
    this.inbox.markRead(this.base, item.id).subscribe({
      next: (r) => {
        if (r?.success) {
          this.items.update((list) => list.map((i) => (i.id === item.id ? { ...i, read: true } : i)));
          this.unread.update((n) => Math.max(0, n - 1));
        }
      },
      error: (e) => console.error('[inbox] failed to mark notification read', e),
    });
  }

  private hasOrder(item: InboxItem): boolean {
    if (!item.dataJson) return false;
    try {
      return !!(JSON.parse(item.dataJson) as Record<string, string>)?.['orderId'];
    } catch {
      return false;
    }
  }

  timeAgo(iso: string): string {
    const t = Date.parse(iso);
    if (Number.isNaN(t)) return '';
    const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
    if (s < 60) return 'now';
    if (s < 3600) return `${Math.floor(s / 60)}m`;
    if (s < 86400) return `${Math.floor(s / 3600)}h`;
    return `${Math.floor(s / 86400)}d`;
  }

  private refreshCount(): void {
    this.inbox.getUnreadCount(this.base).subscribe({
      next: (r) => { if (r?.success) this.unread.set(r.data ?? 0); },
      error: (e) => console.error('[inbox] failed to load unread count', e),
    });
  }

  private loadList(): void {
    this.loading.set(true);
    this.error.set(false);
    this.inbox.getInbox(this.base, 20).subscribe({
      next: (r) => {
        if (r?.success) this.items.set(r.data ?? []);
        else this.error.set(true);
        this.loading.set(false);
      },
      error: (e) => {
        console.error('[inbox] failed to load notifications', e);
        this.error.set(true);
        this.loading.set(false);
      },
    });
  }

  @HostListener('document:click', ['$event'])
  onDocClick(e: MouseEvent): void {
    if (!this.elRef.nativeElement.contains(e.target)) this.open.set(false);
  }

  @HostListener('document:keydown.escape')
  onEsc(): void {
    this.open.set(false);
  }
}
